package one.healthium.cali.presentation.feature.today

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import one.healthium.cali.paytone_18
import one.healthium.cali.presentation.feature.today.components.MealPlanCard
import one.healthium.cali.presentation.feature.today.components.TodayDailyProgress
import one.healthium.cali.presentation.feature.today.components.TodayUserProfile
import one.healthium.healthKit.ui.HealthKitConnectBanner
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.ui.components.ContentRenderer
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.rateus.data.ShowAppRateForStart
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.TodayViewModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.theme.AppTheme
import org.koin.compose.viewmodel.koinViewModel

interface TodayEventDelegate {
    fun onMealPlanClicked(id: String)
    fun onNotificationsClicked()
    fun onActionClicked(action: ActionModel)
}

@Composable
fun TodayScreen(eventDelegate: TodayEventDelegate) {
    val viewModel = koinViewModel<TodayViewModel>()
    val state by viewModel.state(ContentOptions.Category("workouts")).collectAsState()

    ShowAppRateForStart()

    TodayScreenContent(
        state = state,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
        healthKitContract = viewModel,
        onNotificationsClicked = eventDelegate::onNotificationsClicked,
        onActionClicked = eventDelegate::onActionClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)

}

@Composable
internal fun TodayScreenContent(
    state: TodayUiState,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    healthKitContract: HealthKitConnectBannerContract,
    onNotificationsClicked: () -> Unit,
    onActionClicked: (ActionModel) -> Unit,
) {
    AppScaffold(Modifier.testTag("today_screen")) {
        ContentRenderer(
            content = state.content?.content ?: emptyList(),
            onActionClicked = onActionClicked,
            isMetrics = state.isMetric,
            headerContent = {
                HeaderContent(
                    state = state,
                    healthKitContract = healthKitContract,
                    onNotificationsClicked = onNotificationsClicked,
                )
            },
            footerContent = {
                Section(text = "Your meal plan")
                MealPlanCard(
                    modifier = Modifier.padding(horizontal = 12.dp),
                    mealPlan = state.mealPlan,
                    onClick = onMealPlanClicked,
                )
                Spacer(Modifier.height(100.dp))
            }
        )
    }
}

@Composable
private fun HeaderContent(
    state: TodayUiState,
    healthKitContract: HealthKitConnectBannerContract,
    onNotificationsClicked: () -> Unit,
) {
    Box(Modifier.statusBarsPadding())
    val userProfile = state.userProfile
    if (userProfile != null) {
        TodayUserProfile(
            userProfile = userProfile,
            onNotificationsClicked = onNotificationsClicked,
        )
    }
    if (state.shouldShowHKBanner) {
        Spacer(Modifier.height(12.dp))
        HealthKitConnectBanner(contract = healthKitContract)
    }
    TodayDailyProgress(
        todayCaloriesCounter = state.todayCaloriesCounter,
        todayStepsCounter = state.todayStepsCounter,
        overallProgress = state.overallProgress,
    )
    Section(text = "Daily workout")
}

@Composable
private fun Section(text: String) {
    Text(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 24.dp, bottom = 12.dp, start = 12.dp, end = 12.dp),
        text = text,
        style = AppTheme.textStyles.paytone_18,
        color = AppTheme.colors.text100,
        maxLines = 1,
    )
}